import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { D, fmtAmt, toUsdt, type AuditChatRow, type AuditStatus, type UsdtRate } from '@wave/shared';
import { useBalanceAudit, useUsdtRates } from '../api/hooks';
import { useRunAuditSnapshot } from '../api/mutations';
import { AuditChatModal } from '../components/modals/AuditChatModal';
import { ArchiveFindingsTab } from '../components/audit/ArchiveFindingsTab';
import {
  btnPrimarySm,
  cardL1,
  filterActive,
  filterIdle,
  inputCls,
  pageContainer,
  pageHeader,
  pageSubtitle,
  pageTitle,
  tableCell,
  tableCellNum,
  tableCls,
  tableHead,
  tableHeadCell,
  tableHeadCellRight,
  tableRow,
  tableWrapper,
} from '../lib/styles';

export const STATUS_META: Record<
  AuditStatus,
  { label: string; icon: string; cls: string }
> = {
  ok: { label: 'Сходится', icon: '✓', cls: 'text-success' },
  mismatch: { label: 'Расхождение', icon: '⚠', cls: 'text-danger' },
  acked: { label: 'Объяснено', icon: '💬', cls: 'text-text2' },
  unmapped: { label: 'Без контрагента', icon: '⛔', cls: 'text-danger' },
  'no-external': { label: 'Нет данных бота', icon: '∅', cls: 'text-warning' },
};

type Filter = 'all' | 'mismatch' | 'acked' | 'unmapped';
type Tab = 'balances' | 'integrity';

/** «вчера 14:00–15:00» из окна divergedAfter..divergedBefore. */
export function fmtDiverged(after: string | null, before: string | null): string {
  if (!before) return '—';
  const fmt = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const yesterday = new Date(today.getTime() - 86400000);
    const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    if (sameDay) return `сегодня ${time}`;
    if (d.toDateString() === yesterday.toDateString()) return `вчера ${time}`;
    return `${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} ${time}`;
  };
  if (!after) return `до ${fmt(before)}`;
  return `${fmt(after)} – ${fmt(before)}`;
}

/** Σ|Δ| в USDT по mismatch-строкам чата (для сортировки/колонки). */
function deltaUsdt(chat: AuditChatRow, rates: UsdtRate[] | undefined): string | null {
  if (!rates) return null;
  const rateBy = new Map(rates.map((r) => [r.ccy, r.rate]));
  const rateByObj = Object.fromEntries(rateBy);
  let sum = D(0);
  for (const row of chat.rows) {
    if (!row.mismatch) continue;
    const rate = rateBy.get(row.ccy);
    if (!rate) return null;
    sum = sum.plus(toUsdt(row.delta, row.ccy, rateByObj));
  }
  return sum.toDecimalPlaces(2).toString();
}

/**
 * Заголовок и переключатель вкладок рендерятся ВЫШЕ содержимого вкладок —
 * в т.ч. выше обоих ранних `return`'ов вкладки «Балансы» (загрузка и
 * «источник не настроен»), иначе вкладка «Целостность» окажется недостижимой
 * ровно в тех состояниях, ради которых она нужна (спека §6).
 */
export function AuditPage() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const [tab, setTab] = useState<Tab>('balances');

  return (
    <div className={pageContainer}>
      <header className={pageHeader}>
        <div>
          <h1 className={pageTitle}>Аудит</h1>
          <p className={pageSubtitle}>Сверка балансов чатов бота и целостность архива сообщений.</p>
        </div>
      </header>

      <div className="mb-6 border-b border-border-subtle">
        <div className="flex gap-1">
          <TabButton active={tab === 'balances'} onClick={() => setTab('balances')}>
            Балансы
          </TabButton>
          <TabButton
            active={tab === 'integrity'}
            onClick={() => {
              setTab('integrity');
              // Модалка чата принадлежит вкладке «Балансы» — при переключении на
              // «Целостность» она не должна висеть поверх чужой вкладки.
              if (chatId) navigate('/audit');
            }}
          >
            Целостность
          </TabButton>
        </div>
      </div>

      {tab === 'balances' && <BalancesTab chatId={chatId} />}
      {tab === 'integrity' && <ArchiveFindingsTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px px-4 py-2 text-sm font-medium ${
        active
          ? 'border-b-2 border-accent text-accent'
          : 'border-b-2 border-transparent text-text2 hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

/** Вкладка «Балансы» — сверка чатов бота с проводками (без изменений в поведении). */
function BalancesTab({ chatId }: { chatId: string | undefined }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useBalanceAudit();
  const { data: rates } = useUsdtRates();
  const snapshot = useRunAuditSnapshot();
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const chats = useMemo(() => {
    let list = data?.chats ?? [];
    if (filter === 'mismatch') list = list.filter((c) => c.status === 'mismatch' || c.status === 'no-external');
    if (filter === 'acked') list = list.filter((c) => c.status === 'acked');
    if (filter === 'unmapped') list = list.filter((c) => c.status === 'unmapped');
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) => c.title.toLowerCase().includes(q) || (c.accountCode ?? '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [data, filter, search]);

  const selected = chatId ? data?.chats.find((c) => c.chatId === chatId) : undefined;

  if (!data && isLoading) {
    return <div className="text-text2">Загрузка…</div>;
  }
  if (data && !data.configured) {
    return <p className={pageSubtitle}>Источник остатков не настроен — задайте его на странице «Балансы».</p>;
  }

  const kpi = data?.kpi;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className={pageSubtitle}>
          Сверка балансов чатов (бот) с расчётными из проводок.
          {data?.lastRunAt && ` Последний снапшот: ${fmtDiverged(null, data.lastRunAt).replace('до ', '')}.`}
        </p>
        <button
          type="button"
          className={btnPrimarySm}
          disabled={snapshot.isPending}
          onClick={() => snapshot.mutate()}
        >
          {snapshot.isPending ? 'Сверяем…' : '⟳ Сверить сейчас'}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-4 py-3 text-sm text-danger">
          Не удалось загрузить сверку: {(error as Error).message}
        </div>
      )}

      {data?.error && (
        <div className="mb-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-4 py-3 text-sm text-danger">
          Источник недоступен: {data.error}. Данные могут быть неполными — попробуйте обновить позже.
        </div>
      )}

      {kpi && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <KpiCard label="Чатов" value={kpi.total} cls="text-text" />
          <KpiCard label="Сходится ✓" value={kpi.ok} cls="text-success" />
          <KpiCard label="Расхождения ⚠" value={kpi.mismatch + kpi.noExternal} cls="text-danger" />
          <KpiCard label="Объяснено 💬" value={kpi.acked} cls="text-text2" />
          <KpiCard label="Вне учёта ⛔" value={kpi.unmapped} cls="text-danger" />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(
          [
            ['all', 'Все'],
            ['mismatch', 'Расхождения'],
            ['acked', 'Объяснённые'],
            ['unmapped', 'Вне учёта'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={filter === key ? filterActive : filterIdle}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
        <input
          className={`${inputCls} ml-auto w-64`}
          placeholder="Поиск по чату или коду…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className={tableWrapper}>
        <table className={tableCls}>
          <thead className={tableHead}>
            <tr>
              <th className={tableHeadCell}>Чат</th>
              <th className={tableHeadCell}>Статус</th>
              <th className={tableHeadCell}>Δ по валютам</th>
              <th className={tableHeadCellRight}>|Δ| USDT</th>
              <th className={tableHeadCell}>Когда разошлось</th>
            </tr>
          </thead>
          <tbody>
            {chats.map((chat) => {
              const meta = STATUS_META[chat.status];
              const mismatchRows = chat.rows.filter((r) => r.mismatch);
              const worst = mismatchRows
                .filter((r) => r.divergedBefore)
                .sort((a, b) => (a.divergedBefore! < b.divergedBefore! ? -1 : 1))[0];
              return (
                <tr
                  key={chat.chatId}
                  className={`${tableRow} cursor-pointer hover:bg-bg3`}
                  onClick={() => navigate(`/audit/${chat.chatId}`)}
                >
                  <td className={tableCell}>
                    <div className="font-medium text-text">{chat.title}</div>
                    <div className="text-xs text-text2">
                      {chat.accountCode ?? 'не привязан'}
                      {chat.inverted && ' · ⇄ инв'}
                    </div>
                  </td>
                  <td className={tableCell}>
                    <span className={`font-medium ${meta.cls}`}>
                      {meta.icon} {meta.label}
                    </span>
                  </td>
                  <td className={tableCell}>
                    <div className="flex flex-wrap gap-1.5">
                      {mismatchRows.length === 0 && <span className="text-text-subtle">—</span>}
                      {mismatchRows.map((r) => (
                        <span
                          key={r.ccy}
                          className={`rounded-md px-2 py-0.5 font-mono text-xs tabular-nums ${
                            r.ack
                              ? 'bg-bg3 text-text2 line-through'
                              : D(r.delta).isNegative()
                                ? 'bg-[var(--green-bg)] text-success'
                                : 'bg-[var(--red-bg)] text-danger'
                          }`}
                        >
                          {D(r.delta).isNegative() ? '' : '+'}
                          {fmtAmt(r.delta, r.ccy)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className={tableCellNum}>{deltaUsdt(chat, rates) ?? '—'}</td>
                  <td className={`${tableCell} text-xs text-text2`}>
                    {worst ? fmtDiverged(worst.divergedAfter, worst.divergedBefore) : '—'}
                  </td>
                </tr>
              );
            })}
            {chats.length === 0 && (
              <tr>
                <td className={`${tableCell} text-text2`} colSpan={5}>
                  Нет чатов по выбранному фильтру.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && <AuditChatModal key={selected.chatId} chat={selected} onClose={() => navigate('/audit')} />}
    </>
  );
}

export function KpiCard({ label, value, cls }: { label: string; value: number | string; cls: string }) {
  return (
    <div className={cardL1}>
      <div className="text-xs font-medium uppercase tracking-wide text-text2">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
